import React, { useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { ImageObject } from '../../../pages/types';
import ConfirmDialog from '../../confirm-dialog';

interface ImageEditorTooltipProps {
  onEdit: () => void;
  onDelete: () => void; // This will now be called AFTER confirmation
  onMove?: () => void; // Optional for now
  onMoveBackward?: () => void;
  onMoveForward?: () => void;
  onMoveToFirst?: () => void;
  onTextEdit: (title: string, altText: string) => void;
  image: ImageObject;
  children: React.ReactElement; // The image component itself
}

const ImageEditorTooltip: React.FC<ImageEditorTooltipProps> = ({
  onEdit,
  onDelete,
  onMove,
  onMoveBackward,
  onMoveForward,
  onMoveToFirst,
  onTextEdit,
  image,
  children,
}) => {
  const [openTextModal, setOpenTextModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [title, setTitle] = useState(image.title || '');
  const [altText, setAltText] = useState(image.altText || '');

  const handleTextEditClick = () => {
    setTitle(image.title || '');
    setAltText(image.altText || '');
    setOpenTextModal(true);
  };

  const handleSaveText = () => {
    onTextEdit(title, altText);
    setOpenTextModal(false);
  };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'inline-block',
          width: '100%',
          height: '100%',
          '&:hover .image-tools': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        }}
      >
        {children}
        <Box
          className="image-tools"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            borderRadius: 1,
            padding: '4px',
            display: 'flex',
            gap: 1,
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.2s',
            zIndex: 10,
            backdropFilter: 'blur(4px)',
          }}
        >
          <Tooltip title="Replace Image">
            <IconButton size="small" onClick={onEdit} sx={{ color: 'white' }}>
              <AutorenewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Image">
            <IconButton
              size="small"
              onClick={() => setOpenDeleteDialog(true)}
              sx={{ color: 'white' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {onMoveToFirst && (
            <Tooltip title="Move to First">
              <IconButton
                size="small"
                onClick={onMoveToFirst}
                sx={{ color: 'white' }}
              >
                <VerticalAlignTopIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onMoveBackward && (
            <Tooltip title="Move Left/Backward">
              <IconButton
                size="small"
                onClick={onMoveBackward}
                sx={{ color: 'white' }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onMoveForward && (
            <Tooltip title="Move Right/Forward">
              <IconButton
                size="small"
                onClick={onMoveForward}
                sx={{ color: 'white' }}
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onMove && (
            <Tooltip title="Move Image">
              <IconButton
                size="small"
                onClick={onMove}
                sx={{ color: 'white', cursor: 'grab' }}
              >
                <OpenWithIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Edit Text">
            <IconButton
              size="small"
              onClick={handleTextEditClick}
              sx={{ color: 'white' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Dialog
        open={openTextModal}
        onClose={() => setOpenTextModal(false)}
        fullWidth
      >
        <DialogTitle>Edit Image Details</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 1,
              width: '90%',
            }}
          >
            <Typography variant="body1" color="textSecondary">
              Caption
            </Typography>
            <TextField
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              multiline
              placeholder="Image Title"
            />
            <Typography variant="body1" color="textSecondary">
              Alt Text
            </Typography>
            <TextField
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              fullWidth
              multiline
              placeholder="Alternative Text"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTextModal(false)}>Cancel</Button>
          <Button onClick={handleSaveText} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={() => {
          onDelete();
          setOpenDeleteDialog(false);
        }}
        title="Delete Image?"
        content="Are you sure you want to delete this image? This action cannot be undone."
      />
    </>
  );
};

export default ImageEditorTooltip;
